import React from "react";
import { Button, Flex, Result } from "@murm/ant";
import styles from "./Fallback.module.css";

/**
 * Recovers from a code-split chunk that is no longer on the origin.
 *
 * `make publish-frontend` runs `aws s3 sync build/ … --delete`, so a deploy removes the previous
 * build's hashed chunks. That was harmless while the app shipped as one bundle: `index.html` is
 * served `no-cache`, so a reload got the new bundle, and a session already running had its single
 * bundle in memory and asked the origin for nothing more.
 *
 * Route-level code splitting changes that. A tab left open across a deploy still holds the old
 * `index.html`, and navigating to a route it has not visited requests a chunk hash that no longer
 * exists — a 404, which React surfaces as a ChunkLoadError and which otherwise renders as a blank
 * region with an error only in the console.
 *
 * A reload fixes it completely, because `index.html` is uncached and points at the current hashes.
 * So: reload once, and only once. The `sessionStorage` latch is what keeps a genuine, repeatable
 * chunk failure (an origin that is actually broken) from becoming a reload loop — the second
 * failure shows the user something instead.
 */

const RELOAD_LATCH = "explorer:chunk-reload-attempted";

/** ChunkLoadError is webpack's; the message forms cover Safari and Firefox, which do not use it. */
export function isChunkLoadError(error) {
  if (!error) return false;
  const name = String(error.name ?? "");
  const message = String(error.message ?? "");
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

function hasAttemptedReload() {
  try {
    return window.sessionStorage.getItem(RELOAD_LATCH) === "1";
  } catch {
    // Private mode or a blocked storage partition: treat as "already tried" so we never loop.
    return true;
  }
}

function markReloadAttempted() {
  try {
    window.sessionStorage.setItem(RELOAD_LATCH, "1");
  } catch {
    /* nothing to do — hasAttemptedReload already fails closed */
  }
}

/** Clear the latch once the app is running, so a later deploy gets its own single retry. */
export function clearChunkReloadLatch() {
  try {
    window.sessionStorage.removeItem(RELOAD_LATCH);
  } catch {
    /* not fatal */
  }
}

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError(error) {
    // Anything that is not a missing chunk belongs to whatever boundary is above this one.
    if (!isChunkLoadError(error)) {
      throw error;
    }
    return { failed: true };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) {
      return;
    }
    if (!hasAttemptedReload()) {
      markReloadAttempted();
      window.location.reload();
    }
  }

  render() {
    if (!this.state.failed) {
      return this.props.children;
    }

    // Reached only when the reload already happened and the chunk is still missing.
    return (
      <Flex align="center" justify="center" className={styles.chunkError}>
        <Result
          status="warning"
          title="This page could not be loaded"
          subTitle="The app was updated while this tab was open. Reloading usually fixes it."
          extra={
            <Button type="primary" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        />
      </Flex>
    );
  }
}

export default ChunkErrorBoundary;
